import { FC, useEffect, useState } from 'react';
import { Grid as KendoGrid, GridCellProps, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { State as ODataState } from '@progress/kendo-data-query';
import myCustomersApi from '../../api/MyCustomersApi';
import { CartDetail, CartDetailRowProps } from 'src/types/customerCartList';
import { CircularProgress } from '@mui/material';
import ShoppingCartUrlCell from '../grid/ShoppingCartUrlCell';
import { useNavigate } from 'react-router';
import OrderApi from 'src/api/OrderApi';
import { ApiReponse } from 'src/types/apiResponse';
import { OrderEntry } from 'src/types/orderEntry';
import useSnackbar from 'src/hooks/useSnackbar';
import { LoadingButton } from '@mui/lab';

const CustomerCartDetail: FC<React.PropsWithChildren<CartDetailRowProps>> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: [{ field: 'customerNumber', value: props.dataItem.customerNumber, operator: 'eq' }],
      logic: 'and',
    },
    skip: 0,
    take: 20,
  });
  const [cartDetail, setCartDetail] = useState<CartDetail[]>([]);

  //Track loading cart ids to show loading state on LoadingButton
  const [loadingCartIds, setLoadingCartIds] = useState<number[]>([]);

  const navigate = useNavigate();
  const { openSnackBar } = useSnackbar();

  const loadCustomerDetail = async () => {
    setIsLoading(true);
    myCustomersApi
      .getCustomerCartDetail(gridDataState)
      .then((response) => {
        setCartDetail(response.value);
        setIsLoading(false);
      })
      .catch(() => {
        setCartDetail([]);
        setIsLoading(false);
      });
  };

  const handleGridDataStateChange = (e: any) => {
    setGridDataState(e.dataState);
  };

  useEffect(() => {
    loadCustomerDetail();
  }, [gridDataState]);

  const shoppingCartUrlCell = (cellProps: GridCellProps) => <ShoppingCartUrlCell {...cellProps} />;

  const createOrderCell = (cellProps: GridCellProps) => {
    const shoppingCartId: number = cellProps.dataItem.shoppingCartId;
    const buttonLoading = loadingCartIds.includes(shoppingCartId);

    return (
      <td style={{ padding: 0 }}>
        <LoadingButton
          fullWidth={true}
          loading={buttonLoading}
          loadingPosition="end"
          sx={{
            border: '1px solid #042312',
            background: '#e2f3ea',
            color: '#042312',
          }}
          onClick={() => handleCreateOrder(shoppingCartId)}
        >
          Create Order
        </LoadingButton>
      </td>
    );
  };

  const handleCreateOrder = (shoppingCartId: number) => {
    console.log('Create order for shoppingCartId:', shoppingCartId);

    setLoadingCartIds((prev) => [...prev, shoppingCartId]);

    OrderApi.createOrderFromCart(shoppingCartId)
      .then((res: ApiReponse) => {
        if (res.success) {
          let sfaOrder: OrderEntry = res.value as OrderEntry;
          navigate(`/${sfaOrder.customerNumber}/OrderEntry/${sfaOrder.sfaOrderId}`);
        } else {
          openSnackBar(`Error creating order from cart: ${res.message}`, 'error', 10000);
          console.error('Error creating order:', res.message);
        }
      })
      .catch((error) => {
        console.error('Error creating order:', error);
      })
      .finally(() => {
        setLoadingCartIds((prev) => prev.filter((id) => id !== shoppingCartId));
      });
  };

  return (
    <div className="progress-table-container">
      <KendoGrid data={cartDetail} {...gridDataState} onDataStateChange={handleGridDataStateChange}>
        {isLoading ? (
          <GridNoRecords>
            <CircularProgress></CircularProgress>
          </GridNoRecords>
        ) : (
          <GridNoRecords>No records available.</GridNoRecords>
        )}
        <GridColumn title="" cells={{ data: createOrderCell }} width="150px" />
        <GridColumn field="username" cells={{ data: shoppingCartUrlCell }} title="User Name" />
        <GridColumn title="Quantity" field="quantity"></GridColumn>
        <GridColumn title="Total" field="cartTotal" format="{0:c}"></GridColumn>
      </KendoGrid>
    </div>
  );
};

export default CustomerCartDetail;
